import { getLocalStorageLanguage } from 'commons';
import { LANGUAGE_MAP, SystemLanguageType } from 'constant/language';

const RAW_ORDER_STATUS_MAP = {
  TO_BE_PAID: 0,
  TO_BE_DELIVERED: 1,
  DELIVERED: 2,
  COMPLETED: 3,
  CANCLED: 4,
  INVALID_ORDER: 5,
  REFUND_ORDER: 6,
} as const;

type OrderStatus =
  (typeof RAW_ORDER_STATUS_MAP)[keyof typeof RAW_ORDER_STATUS_MAP];

export type OrderStatusOption = {
  value: OrderStatus;
  label: string;
};

const isValidOrderStatus = (status: number): status is OrderStatus => {
  return Object.values(RAW_ORDER_STATUS_MAP).includes(status as OrderStatus);
};

type OrderStatusConfig = {
  color: string;
  label: Record<SystemLanguageType, string>;
};

const RAW_ORDER_STATUS_CONFIG_MAP: Record<OrderStatus, OrderStatusConfig> = {
  [RAW_ORDER_STATUS_MAP.TO_BE_PAID]: {
    color: '#b2c3d1',
    label: {
      [LANGUAGE_MAP.EN]: 'To Be Paid',
      [LANGUAGE_MAP.JA]: '支払い待ち',
      [LANGUAGE_MAP.ZH_CN]: '待付款',
    },
  },
  [RAW_ORDER_STATUS_MAP.TO_BE_DELIVERED]: {
    color: '#ff8143',
    label: {
      [LANGUAGE_MAP.EN]: 'To Be Delivered',
      [LANGUAGE_MAP.JA]: '配達待ち',
      [LANGUAGE_MAP.ZH_CN]: '待发货',
    },
  },
  [RAW_ORDER_STATUS_MAP.DELIVERED]: {
    color: '#465dd2',
    label: {
      [LANGUAGE_MAP.EN]: 'Delivered',
      [LANGUAGE_MAP.JA]: '配達済',
      [LANGUAGE_MAP.ZH_CN]: '已发货',
    },
  },
  [RAW_ORDER_STATUS_MAP.COMPLETED]: {
    color: '#27941d',
    label: {
      [LANGUAGE_MAP.EN]: 'Completed',
      [LANGUAGE_MAP.JA]: '完成',
      [LANGUAGE_MAP.ZH_CN]: '已完成',
    },
  },
  [RAW_ORDER_STATUS_MAP.CANCLED]: {
    color: '#ff3b1b',
    label: {
      [LANGUAGE_MAP.EN]: 'Canceled',
      [LANGUAGE_MAP.JA]: 'キャンセル',
      [LANGUAGE_MAP.ZH_CN]: '已取消',
    },
  },
  [RAW_ORDER_STATUS_MAP.INVALID_ORDER]: {
    color: '#ff3b1b',
    label: {
      [LANGUAGE_MAP.EN]: 'Invalid Order',
      [LANGUAGE_MAP.JA]: '無効な注文',
      [LANGUAGE_MAP.ZH_CN]: '无效订单',
    },
  },
  [RAW_ORDER_STATUS_MAP.REFUND_ORDER]: {
    color: '#ff3b1b',
    label: {
      [LANGUAGE_MAP.EN]: 'Refund Order',
      [LANGUAGE_MAP.JA]: '払い戻し注文',
      [LANGUAGE_MAP.ZH_CN]: '退款订单',
    },
  },
} as const;

export const ORDER_STATUS_MAP = new Proxy(RAW_ORDER_STATUS_MAP, {
  ownKeys() {
    throw new Error('禁止遍历 ORDER_STATUS_MAP');
  },
});

export const ORDER_STATUS_CONFIG_MAP = new Proxy(RAW_ORDER_STATUS_CONFIG_MAP, {
  ownKeys() {
    throw new Error('禁止遍历 ORDER_STATUS_CONFIG_MAP');
  },
});

export const getOrderStatusConfig = (status: number) => {
  if (!isValidOrderStatus(status)) {
    throw new Error(`Invalid order status: ${status}`);
  }

  return ORDER_STATUS_CONFIG_MAP[status];
};

export const getOrderStatusOptions = (
  language: SystemLanguageType = getLocalStorageLanguage(),
): OrderStatusOption[] =>
  Object.values(RAW_ORDER_STATUS_MAP).map((value) => ({
    value,
    label: RAW_ORDER_STATUS_CONFIG_MAP[value].label[language],
  }));
